import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Tooltip, Alert, FormControl, InputLabel, Select, MenuItem,
    Grid, Autocomplete
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function GestionCuentasBancarias() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    const [cuentas, setCuentas] = useState([]);
    const [bancos, setBancos] = useState([]);
    const [monedas, setMonedas] = useState([]);
    const [cuentasContables, setCuentasContables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [cuentaActual, setCuentaActual] = useState({
        idCuentaBancaria: 0,
        cuentaBancaria: '',
        idBanco: '',
        idMoneda: '',
        idCuentaContable: null,
        idCuentaContableNew: null
    });

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        await Promise.all([
            cargarCuentasBancarias(),
            cargarBancos(),
            cargarMonedas(),
            cargarCuentasContables()
        ]);
    };

    const cargarCuentasBancarias = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${server}/ChkBancosCuentasBancarias`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                const cuentasArray = data.$values || data;
                console.log('Cuentas bancarias cargadas:', cuentasArray);
                setCuentas(cuentasArray);
            }
        } catch (error) {
            console.error('Error al cargar cuentas bancarias:', error);
        } finally {
            setLoading(false);
        }
    };

    const cargarBancos = async () => {
        try {
            const response = await fetch(`${server}/ChkBancoes/catalogo`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setBancos(data.$values || data);
            }
        } catch (error) {
            console.error('Error al cargar bancos:', error);
        }
    };

    const cargarMonedas = async () => {
        try {
            const response = await fetch(`${server}/MgMonedas`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                const monedasArray = data.$values || data;
                console.log('Monedas cargadas:', monedasArray);
                setMonedas(monedasArray);
            } else {
                console.error('Error en respuesta de monedas:', response.status);
                Swal.fire('Error', 'No se pudieron cargar las monedas', 'error');
            }
        } catch (error) {
            console.error('Error al cargar monedas:', error);
            Swal.fire('Error', 'Error al cargar las monedas', 'error');
        }
    };

    const cargarCuentasContables = async () => {
        try {
            const response = await fetch(`${server}/CtbPlanDeCuentas/detalle`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                const cuentasArray = data.$values || data;
                // Filtrar solo cuentas activas y de nivel 5 (detalle)
                const cuentasActivas = cuentasArray.filter(c => c.activa === true || c.activa === null);
                console.log('Cuentas contables nivel 5 cargadas:', cuentasActivas.length);
                setCuentasContables(cuentasActivas);
            } else {
                console.error('Error en respuesta de cuentas contables:', response.status);
                Swal.fire('Error', 'No se pudieron cargar las cuentas contables', 'error');
            }
        } catch (error) {
            console.error('Error al cargar cuentas contables:', error);
            Swal.fire('Error', 'Error al cargar las cuentas contables', 'error');
        }
    };

    const abrirDialogoNuevo = () => {
        setCuentaActual({
            idCuentaBancaria: 0,
            cuentaBancaria: '',
            idBanco: '',
            idMoneda: '',
            idCuentaContable: null,
            idCuentaContableNew: null
        });
        setModoEdicion(false);
        setOpenDialog(true);
    };

    const abrirDialogoEditar = (cuenta) => {
        setCuentaActual({
            idCuentaBancaria: cuenta.idCuentaBancaria,
            cuentaBancaria: cuenta.cuentaBancaria,
            idBanco: cuenta.idBanco,
            idMoneda: cuenta.idMoneda,
            idCuentaContable: cuenta.idCuentaContable,
            idCuentaContableNew: cuenta.idCuentaContableNew,
            ingresadoPor: cuenta.ingresadoPor,
            fechaIngreso: cuenta.fechaIngreso
        });
        setModoEdicion(true);
        setOpenDialog(true);
    };

    const cerrarDialogo = () => {
        setOpenDialog(false);
        setCuentaActual({
            idCuentaBancaria: 0,
            cuentaBancaria: '',
            idBanco: '',
            idMoneda: '',
            idCuentaContable: null,
            idCuentaContableNew: null
        });
    };

    const guardarCuenta = async () => {
        if (!cuentaActual.cuentaBancaria.trim() || !cuentaActual.idBanco || !cuentaActual.idMoneda) {
            Swal.fire('Validación', 'Los campos Cuenta Bancaria, Banco y Moneda son obligatorios', 'warning');
            return;
        }

        setLoading(true);
        try {
            const usuarioPadded = (user.codigoUsuario || '').padEnd(20, ' ');
            
            const cuentaData = {
                ...cuentaActual,
                modificacion: modoEdicion ? 2 : 1,
                ingresadoPor: modoEdicion ? cuentaActual.ingresadoPor : usuarioPadded,
                modificadoPor: modoEdicion ? usuarioPadded : null,
                fechaIngreso: modoEdicion ? cuentaActual.fechaIngreso : new Date().toISOString(),
                fechaModificacion: modoEdicion ? new Date().toISOString() : null
            };

            const url = modoEdicion 
                ? `${server}/ChkBancosCuentasBancarias/${cuentaActual.idCuentaBancaria}`
                : `${server}/ChkBancosCuentasBancarias`;

            const response = await fetch(url, {
                method: modoEdicion ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(cuentaData)
            });

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: modoEdicion ? 'Cuenta actualizada' : 'Cuenta creada',
                    timer: 1500,
                    showConfirmButton: false
                });
                cerrarDialogo();
                cargarCuentasBancarias();
            } else {
                const errorData = await response.json().catch(() => ({}));
                Swal.fire('Error', errorData.message || 'Error al guardar la cuenta', 'error');
            }
        } catch (error) {
            console.error('Error al guardar cuenta:', error);
            Swal.fire('Error', 'Error al guardar la cuenta', 'error');
        } finally {
            setLoading(false);
        }
    };

    const eliminarCuenta = async (id) => {
        const resultado = await Swal.fire({
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });

        if (resultado.isConfirmed) {
            setLoading(true);
            try {
                const response = await fetch(`${server}/ChkBancosCuentasBancarias/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Cuenta eliminada',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    cargarCuentasBancarias();
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    Swal.fire('Error', errorData.message || 'No se puede eliminar la cuenta', 'error');
                }
            } catch (error) {
                console.error('Error al eliminar cuenta:', error);
                Swal.fire('Error', 'Error al eliminar la cuenta', 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="right_col" role="main">
            <div className="page-title">
                <div className="title_left">
                    <h3>Gestión de Cuentas Bancarias</h3>
                </div>
            </div>

            <div className="clearfix"></div>

            <div className="row">
                <div className="col-md-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Lista de Cuentas Bancarias</h2>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                onClick={abrirDialogoNuevo}
                                style={{ float: 'right' }}
                                disabled={!tienePermiso('Editar Cuenta Bancaria')}
                            >
                                Nueva Cuenta
                            </Button>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            {loading && <Alert severity="info">Cargando...</Alert>}
                            
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Cuenta Bancaria</TableCell>
                                            <TableCell>Banco</TableCell>
                                            <TableCell>Moneda</TableCell>
                                            <TableCell>Cuenta Contable (Legacy)</TableCell>
                                            <TableCell>Cuenta Contable (Nueva)</TableCell>
                                            <TableCell align="center">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cuentas.filter(c => c.modificacion !== 3).map((cuenta) => (
                                            <TableRow key={cuenta.idCuentaBancaria}>
                                                <TableCell>{cuenta.idCuentaBancaria}</TableCell>
                                                <TableCell>{cuenta.cuentaBancaria}</TableCell>
                                                <TableCell>{cuenta.nombreBanco}</TableCell>
                                                <TableCell>{cuenta.descripcionMoneda}</TableCell>
                                                <TableCell>{cuenta.cuentaContable || '-'}</TableCell>
                                                <TableCell>
                                                    {cuenta.idCuentaContableNew 
                                                        ? (() => {
                                                            const cuentaEncontrada = cuentasContables.find(c => 
                                                                (c.idCuenta || c.IdCuenta) === cuenta.idCuentaContableNew
                                                            );
                                                            return cuentaEncontrada 
                                                                ? `${cuentaEncontrada.codigo || cuentaEncontrada.Codigo} - ${cuentaEncontrada.descripcion || cuentaEncontrada.Descripcion}`
                                                                : cuenta.idCuentaContableNew;
                                                          })()
                                                        : '-'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => abrirDialogoEditar(cuenta)}
                                                            disabled={!tienePermiso('Editar Cuenta Bancaria')}
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Eliminar">
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => eliminarCuenta(cuenta.idCuentaBancaria)}
                                                            disabled={!tienePermiso('Eliminar Cuenta Bancaria')}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog para crear/editar cuenta */}
            <Dialog open={openDialog} onClose={cerrarDialogo} maxWidth="md" fullWidth>
                <DialogTitle>{modoEdicion ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Número de Cuenta *"
                                    fullWidth
                                    value={cuentaActual.cuentaBancaria}
                                    onChange={(e) => setCuentaActual({ ...cuentaActual, cuentaBancaria: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Banco *</InputLabel>
                                    <Select
                                        value={cuentaActual.idBanco}
                                        onChange={(e) => setCuentaActual({ ...cuentaActual, idBanco: e.target.value })}
                                        label="Banco *"
                                    >
                                        <MenuItem value="">Seleccionar</MenuItem>
                                        {bancos.map((banco) => (
                                            <MenuItem key={banco.idBanco} value={banco.idBanco}>
                                                {banco.nombreBanco}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Moneda *</InputLabel>
                                    <Select
                                        value={cuentaActual.idMoneda}
                                        onChange={(e) => setCuentaActual({ ...cuentaActual, idMoneda: e.target.value })}
                                        label="Moneda *"
                                    >
                                        <MenuItem value="">Seleccionar</MenuItem>
                                        {monedas.map((moneda) => (
                                            <MenuItem key={moneda.idMoneda || moneda.IdMoneda} value={moneda.idMoneda || moneda.IdMoneda}>
                                                {moneda.descripcionMoneda || moneda.DescripcionMoneda}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={cuentasContables}
                                    getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                                    value={cuentasContables.find(c => c.idCuenta === cuentaActual.idCuentaContableNew) || null}
                                    onChange={(e, newValue) => {
                                        setCuentaActual({ ...cuentaActual, idCuentaContableNew: newValue?.idCuenta || null });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Cuenta Contable (Nueva)" />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.idCuenta === value.idCuenta}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    La cuenta contable legacy se mantiene para compatibilidad. Use "Cuenta Contable (Nueva)" para nuevas configuraciones.
                                </Alert>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarDialogo}>Cancelar</Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={guardarCuenta}
                        disabled={loading || !tienePermiso('Editar Cuenta Bancaria')}
                    >
                        {loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default GestionCuentasBancarias;
